import { Component, ReactNode } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/MonthlyBarChart/MonthlyBarChart.css'
import * as moment from 'moment'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { StatusType } from 'interfaces'

export type SingleMonthBarChartProps = {
  offset: number
  date: string
  memberValue: number
  peerValue: number
  otherValue: number
  memberProjected?: number
  peerProjected?: number
  otherProjected?: number
  hideValues: boolean
  noValues?: boolean
  withComparison: boolean
  getBarHeight: (value: number) => number
  onClick?: () => void
  onMouseOver?: () => void
  onMouseLeave?: () => void
}

type BarDirection = 'up' | 'down'

// calculated height - height that we calculate with passed getBarHeight function
// render height - height that we calculate considering min heights
type BarHeight = {
  // it's render height
  height: number
  // amend is difference between calculated height and rendered height
  // we need it only for calculating position
  // so we have it only when we render bar under axis
  amend: number
  // null when we don't render bar
  direction: BarDirection | null
}

type BarHeights = {
  actualBar: BarHeight
  projectedBar: BarHeight
}

// this function is important only in terms of this chart:
// we consider 0 as having both signs
const isSameSign = (first: number, second: number) =>
  first === 0 || second === 0 || first >= 0 === second >= 0

// relative, percents
const MIN_ACTUAL_BAR_ZERO_HEIGHT = 1.5
const MIN_BAR_NON_ZERO_HEIGHT = 2.5
const MIN_PROJECTED_BAR_HEIGHT = 1

class SingleMonthBarChart extends Component<SingleMonthBarChartProps, {}> {

  render () {
    const {
      memberValue, peerValue, otherValue,
      memberProjected, peerProjected, otherProjected,
      date,
      onClick,
      onMouseOver,
      onMouseLeave,
    } = this.props

    return (
      <div
        className={ styles.barsContainer }
        onClick={ onClick }
        onMouseOver={ onMouseOver }
        onMouseLeave={ onMouseLeave }
      >
        <div className={ styles.monthLabel }>{ moment(date).format('MMM') }</div>
        { this.renderBar(memberValue, 'member', memberProjected || 0) }
        { this.renderBar(peerValue, 'peers', peerProjected || 0) }
        { this.renderBar(otherValue, 'others', otherProjected || 0) }
      </div>
    )
  }

  // frontbar is actual bar if !!projected
  renderFrontBar (bars: BarHeights, displayValue: ReactNode, theme: StatusType) {
    const { actualBar, projectedBar } = bars
    const { noValues } = this.props

    const atTop = projectedBar.direction === 'down'
    // strict means bars are directed to different directions
    const isDirectionStrict = actualBar.direction !== projectedBar.direction

    const frontBarClassName = cn(styles.frontBar, styles[theme], {
      [styles.frontBarAtTopStrict]: atTop && isDirectionStrict,
      [styles.frontBarAtBottomStrict]: !atTop && isDirectionStrict,
      [styles.frontBarAtTop]: atTop,
      [styles.frontBarAtBottom]: !atTop,
    })

    const actualHeight = bars.actualBar.height
    const projectedHeight = bars.projectedBar.height
    const frontBarHeight = Math.min(100, actualHeight / (actualHeight + projectedHeight) * 100)

    return (
      <div
        className={ frontBarClassName }
        style={{ height: frontBarHeight + '%' }}
      >
        { !noValues && displayValue }
      </div>
    )
  }

  renderBar (actual: number, theme: StatusType, projected: number) {
    const { noValues, hideValues, withComparison } = this.props

    const sameSign = isSameSign(actual, projected)
    // render projected only when it exists & not render when actual covers projected
    const shouldRenderProjected = !!projected && !(
      sameSign && Math.abs(projected) <= Math.abs(actual)
    )

    const isMemberBar = theme === 'member'
    const bars = this.getBarHeights(actual, projected, shouldRenderProjected)
    const { actualBar, projectedBar } = bars
    const totalHeight = Math.min(100, actualBar.height + projectedBar.height)
    const totalAmend = actualBar.amend + projectedBar.amend
    const top = this.getTop(actual, projected, totalAmend)

    const memberDisplayValue = isMemberBar && (
      <span className={ styles.value }>{ formatUSCurrency(actual) }</span>
    )

    const barClasses = cn(styles.bar, styles[theme], {
      [styles.thinBar]: !isMemberBar,
      [styles.backBar]: isMemberBar,
      [styles.light]: shouldRenderProjected,
      [styles.negative]: shouldRenderProjected
        ? (sameSign ? actual < 0 : projected < 0)
        : actual < 0,
      [styles.hide]: !withComparison && !isMemberBar,
    })

    return (
      <div style={{ height: totalHeight + '%', top }} className={ barClasses }>
        { !noValues && !hideValues && !shouldRenderProjected && memberDisplayValue }
        { shouldRenderProjected && this.renderFrontBar(bars, memberDisplayValue, theme) }
      </div>
    )
  }

  private getBarHeight (barValue: number, minHeight: number, barDirection: BarDirection): BarHeight {
    const { getBarHeight } = this.props

    const calculatedHeight = getBarHeight(Math.abs(barValue))
    const calculatedAmend = Math.max(minHeight - calculatedHeight, 0)
    const renderHeight = calculatedHeight + calculatedAmend
    const renderAmend = barDirection === 'down' ? calculatedAmend : 0

    return {
      height: renderHeight,
      amend: renderAmend,
      direction: barDirection,
    }
  }

  private getBarHeights (actual: number, projected: number, shouldRenderProjected: boolean): BarHeights {
    const sameSign = isSameSign(actual, projected)
    const projDirection = shouldRenderProjected
      ? (projected > 0 ? 'up' : 'down')
      : null
    const actualDirection = actual === 0
      ? (projDirection || 'up')
      : (actual > 0 ? 'up' : 'down')

    const minActualHeight = Math.abs(actual) === 0
      ? MIN_ACTUAL_BAR_ZERO_HEIGHT
      : MIN_BAR_NON_ZERO_HEIGHT
    const actualBar = this.getBarHeight(actual, minActualHeight, actualDirection)

    const minProjHeight = sameSign
      ? MIN_PROJECTED_BAR_HEIGHT
      : MIN_BAR_NON_ZERO_HEIGHT
    const projBarValue = sameSign
      ? projected - actual
      : projected
    const projectedBar: BarHeight = shouldRenderProjected && projDirection
      ? this.getBarHeight(projBarValue, minProjHeight, projDirection)
      : { height: 0, amend: 0, direction: projDirection }

    return {
      actualBar,
      projectedBar,
    }
  }

  private getTop (actual: number, projected: number, amend: number): string {
    const { offset, getBarHeight } = this.props
    const bottomPointValue = Math.min(0, actual, projected || 0)
    const bottomPointHeight = bottomPointValue === 0
      ? 0
      : Math.abs(getBarHeight(bottomPointValue))

    return bottomPointHeight + offset + amend + '%'
  }
}

export default SingleMonthBarChart
